import { makeStyles } from '@material-ui/core/styles'
import moment from 'moment'
import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { getVisitsByFacilityId } from '../../actions/visits/visits.actions'
import { ViewAddOnDialog } from '../../components/Dialogs'
import { ActivitiesTable } from '../../components/Tables/ActivitiesTable'
import { STATUS_ID, filterAddOnsByTimeBlock } from '../../utils'
import Loading from '../common/Loading'

const useStyles = makeStyles((theme) => ({
	root: {
		flexGrow: 1
	}
}))

const sortOrder = {
	morning: 0,
	midday: 1,
	evening: 2,
	night: 3
}

const Activities = (props) => {
	const classes = useStyles()
	const dispatch = useDispatch()

	const facility = useSelector((state) => state.specific.facilities.selected)
	const visits = useSelector((state) => state.specific.visits.visits)
	const pets = useSelector((state) => state.specific.visits.pets)
	const loading = useSelector((state) => state.specific.visits.loading)

	const selectedFilters = useSelector((state) => state.specific.visits.selectedActivityFilters)

	const [filteredVisits, setFilteredVisits] = useState([])
	const [date, setDate] = useState(moment().format('YYYY-MM-DD'))
	const [results, setResults] = useState(0)

	const [dialog, setDialog] = useState(false)

	const [addOn, setAddOn] = useState(null)
	const [addOnServices, setAddOnServices] = useState(null)
	const [pet, setPet] = useState(null)

	const changeDate = (type) => {
		if (type === 'add') {
			setDate(moment(date).add(1, 'd').format('YYYY-MM-DD'))
		} else if (type === 'subtract') {
			setDate(moment(date).subtract(1, 'd').format('YYYY-MM-DD'))
		}
	}

	useEffect(() => {
		if (facility) {
			dispatch(getVisitsByFacilityId(facility, date))
		}
	}, [date, facility])

	useEffect(() => {
		let visitAddOns = []

		if (visits && visits.length > 0) {
			setFilteredVisits(visits.filter((v) => v.status && v.status.id !== 7))
			let ourVisits = visits.filter((v) => v.status && v.status.id !== 7)
			visitAddOns = ourVisits.length
				? ourVisits.reduce((acc, cur) => {
						return {
							add_on_services: [...acc.add_on_services, ...cur.add_on_services]
						}
				  }).add_on_services
				: []
		}

		setAddOnServices(visitAddOns)
	}, [visits])

	const openDialog = (addOn, pet) => {
		setDialog(true)
		setAddOn(addOn)
		setPet(pet)
	}

	const getAddons = () => (addOnServices ? addOnServices.filter((a) => !a.date || a.date === date) : [])

	const setIsShown = (shown) => {
		setDialog(shown)
	}

	const allAddOns = useMemo(() => {
		if (loading || !addOnServices || !visits) {
			return []
		}

		let adds = addOnServices.filter((a) => !a.date || a.date === date)

		const allowedApptTypeWithMeds = ['boarding', 'compatibility_session', 'daycare']

		visits
			.filter((v) => v.status && ![STATUS_ID.Cancelled, STATUS_ID['No Show']].includes(v.status.id))
			.forEach((v, i) => {
				if (v.medications) {
					v.medications.forEach((med, i) => {
						if (med.date === date && !v.not_present_meds?.includes(med._id))
							adds.push({ ...med, visit_id: v._id })
					})
				}
				const isMedAllowed = allowedApptTypeWithMeds.includes(v?.type)
				const isBoarding = v?.type === 'boarding'
				if (v.pets) {
					v.pets.forEach((pet) => {
						if (pet.food_schedule && isBoarding)
							pet.food_schedule.forEach((t, i) => {
								let find = adds.find(
									(a) =>
										a.category === 'meal' &&
										a.pet_id === pet._id &&
										a.date === date &&
										a.time === t
								)
								if (!find)
									adds.push({
										category: 'meal',
										name: 'Meal',
										pet_id: pet._id,
										date: date,
										time: t,
										visit_id: v._id
									})
							})

						if (pet.medication && isMedAllowed)
							pet.medication.forEach((med, i) => {
								if (
									med.times_of_day &&
									med.frequency === 'Daily' &&
									!v.not_present_meds?.includes(med._id)
								) {
									med.times_of_day.forEach((ti, i) => {
										let alreadyInArr = adds.find(
											(m) =>
												m.med_id === med._id &&
												m.pet_id === pet._id &&
												m.date === date &&
												m.time === ti
										)
										if (!alreadyInArr)
											adds.push({
												...med,
												date,
												time: ti,
												pet_id: pet._id,
												med_id: med._id,
												visit_id: v._id
											})
									})
								}
							})
					})
				}
			})

		adds = adds
			.map((a) => {
				let pet
				if (a.pet_id) pet = pets.find((p) => p._id == a.pet_id)
				let name
				if (a.category === 'meal') name = 'Meal'
				if (a.med_id) name = `Meds - ${a.name}`
				return {
					...a,
					name: name ? name : a.name,
					pet_name: pet ? pet.name : '',
					status: a.category === 'meal' && a.consumption ? 'completed' : a.status
				}
			})
			.sort((a, b) => {
				return sortOrder[a.time] - sortOrder[b.time] || a.pet_name.localeCompare(b.name)
			})

		adds = adds.filter((a) => {
			let visit
			let pet
			if (a.visit_id) visit = visits.find((v) => v._id == a.visit_id)
			if (visit) {
				pet = visit.pets.find((p) => p._id == a.pet_id)

				// removing addons that belong to visits with status no show
				if ([STATUS_ID.Cancelled, STATUS_ID['No Show']].includes(visit.status.id)) return false
			}

			if (a.skipped) return false

			return (!a.status || a.status === 'waiting') && pet && pet.status && pet.status.checked_out
				? false
				: true
		})

		adds = adds.filter((a) => {
			let visit
			let pet
			if (a.visit_id) visit = visits.find((v) => v._id == a.visit_id)
			if (!visit) return true

			return filterAddOnsByTimeBlock(a, visit)
		})

		if (selectedFilters['meals']) {
			adds = adds.filter((a) => (a.category && a.category === 'meal' ? true : false))
		}

		if (selectedFilters['meds']) {
			adds = adds.filter((a) => a.med_id)
		}

		if (selectedFilters['status'] && selectedFilters['status'].length) {
			adds = adds.filter((a) => selectedFilters['status'].includes(!a.status ? 'waiting' : a.status))
		}

		if (selectedFilters['shift'] && selectedFilters['shift'].length) {
			adds = adds.filter((a) => selectedFilters['shift'].includes(a.time.toLowerCase()))
		}

		if (selectedFilters['add-ons'] && selectedFilters['add-ons'].length) {
			adds = adds.filter((a) => selectedFilters['add-ons'].includes(a.name))
		}

		if (selectedFilters['appointment_status'] && selectedFilters['appointment_status'].length) {
			adds = adds.filter((a) => {
				let visit
				if (a.visit_id) visit = visits.find((v) => v._id == a.visit_id)
				if (!visit || !visit.status.id) return true

				const flag = selectedFilters['appointment_status'].includes(visit.status.id)

				// console.info({ visit, flag })
				return flag
			})
		}

		// filtering grooming_service addons
		adds = adds.filter((a) => !a.grooming_service)

		return adds
	}, [addOnServices, visits, loading, selectedFilters])

	return (
		<div className={classes.root}>
			{facility && visits && addOnServices ? (
				<div className={classes.visitsTableContainer}>
					<ActivitiesTable
						addOnTypes={facility.addOnTypes || []}
						addons_categories={facility.addons_categories.filter((a) => !a.grooming_service) || []}
						allAddOns={allAddOns}
						pets={pets}
						date={date}
						visits={filteredVisits || []}
						changeDate={changeDate}
						setDate={setDate}
						results={results}
						openAddOnDialog={(addOn, pet) => openDialog(addOn, pet)}
					/>
				</div>
			) : (
				<Loading />
			)}
			<ViewAddOnDialog
				isShown={dialog}
				setIsShown={setIsShown}
				addOn={addOn}
				pet={pet}
				visits={visits ? visits.filter((v) => v.status && v.status.id !== 7) : []}
			/>
		</div>
	)
}

export default Activities
